
import { Loader2 } from 'lucide-react';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Button } from '@/components/ui/button';
import { useEffect, useState } from 'react';

interface DeleteApplicationDialogProps {
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
  onConfirm: () => Promise<void>;
  isActionInProgress: boolean;
  errorMessage?: string | null;
}

export const DeleteApplicationDialog = ({
  isOpen,
  onOpenChange,
  onConfirm,
  isActionInProgress,
  errorMessage = null,
}: DeleteApplicationDialogProps) => {
  const [localErrorMessage, setLocalErrorMessage] = useState<string | null>(null);

  // Update local error message when prop changes
  useEffect(() => {
    setLocalErrorMessage(errorMessage);
  }, [errorMessage]);

  // Clear error message when dialog opens/closes
  useEffect(() => {
    if (!isOpen) {
      // Clear error message when dialog closes
      setTimeout(() => setLocalErrorMessage(null), 300);
    }
  }, [isOpen]);

  const handleDeleteClick = async (e: React.MouseEvent) => {
    e.preventDefault();
    setLocalErrorMessage(null);
    try {
      await onConfirm();
    } catch (error: any) {
      setLocalErrorMessage(error.message || "An unexpected error occurred");
    }
  };

  return (
    <AlertDialog open={isOpen} onOpenChange={onOpenChange}>
      <AlertDialogContent className="max-w-md">
        <AlertDialogHeader>
          <AlertDialogTitle>Delete Application</AlertDialogTitle>
          <AlertDialogDescription>
            This will permanently delete this application and all associated data.
            This action cannot be undone.
          </AlertDialogDescription>
        </AlertDialogHeader>
        
        {localErrorMessage && (
          <div className="bg-destructive/15 p-3 rounded-md text-sm text-destructive mb-3">
            {localErrorMessage}
          </div>
        )}
        
        <AlertDialogFooter>
          <AlertDialogCancel disabled={isActionInProgress}>Cancel</AlertDialogCancel>
          <AlertDialogAction 
            onClick={handleDeleteClick}
            disabled={isActionInProgress}
            className="bg-destructive hover:bg-destructive/90"
          >
            {isActionInProgress ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Deleting...
              </>
            ) : (
              "Delete"
            )}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
};
